// EntryTipModal — the popup that opens when the user taps the `?`
// pill on a Logger entry. Replaces the 1.67.5 inline-description
// toggle with a richer surface: description + dual worked-muscles
// bodymap + ranked alternative exercises (tap an alt to swap).
//
// Design picks come from feedback_quick_tip_pill_layout (memory):
// vertical layout description → dual bodymap (front+back side-by-
// side simultaneously) → alternatives list. Decided 2026-05-19.
//
// Composition over creation: this Modal composes existing pieces.
// Description renderer = pickDescription() from @heraion/shared.
// Dual bodymap = the existing <BodyMap layout='dual'> that 1.66.0
// WorkoutBodyMap already uses. Alternatives ranking =
// rankSwapCandidates() that ExerciseSwapPicker already uses. The
// parent owns the swap-action wiring (loadedSides + isCardio
// branching, sticky bilateral preference) so swap behaviour stays
// identical to the dedicated swap picker.

import { useCallback, useMemo } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Trans, useTranslation } from 'react-i18next';

import {
  PAIN_SEVERITY_COLOURS,
  WORKED_MUSCLE_COLOURS,
  exercisePainSeverity,
  pickDescription,
  rankSwapCandidates,
  workedMusclesFromExercises,
  type Exercise,
  type Locale,
  type MuscleSlug,
} from '@heraion/shared';

import { ScaleLegend } from '../index';

import { BodyMap } from '../bodymap/BodyMap';
import { ExerciseVisualPager, type AnimationSourceFn } from './ExerciseVisualPager';
import { makeStyles } from './EntryTipModal.styles';
import { HistoryPanel } from './HistoryPanel';

import { showsPerImplementHint } from './loadingHint';
import { useExerciseName } from './exerciseName';
import { useTheme } from './theme';

// ⭐ LG-59 — how many past sets the card's history page lists. Fewer than the
// logger's sheet: this page shares its height with the animation and the
// bodymap, and a page that grows past them stretches all three.
const CARD_SET_ROWS = 8;

// Top-N alternatives to surface. rankSwapCandidates returns the full
// sorted list; we cap to keep the popup readable on phone screens.
// User can still open the dedicated swap picker (⇄ icon) for the
// full list when none of these match.
const ALT_LIMIT = 6;

const SUPPORTED_LOCALES: ReadonlyArray<Locale> = ['en', 'it', 'fr', 'de', 'ru'];

// Canonical muscle slug → display label. Slugs are domain-stable English
// (kebab-case). For v1 we surface them lightly formatted; a future ship
// can wire localized labels through a canonical → catalog → muscleName
// inverse map if Antenore asks.
const formatMuscleSlug = (slug: MuscleSlug): string =>
  slug.replace(/-/g, ' ');

const normaliseLocale = (lang: string | undefined): Locale => {
  const base = (lang ?? 'en').split('-')[0] ?? 'en';
  return (SUPPORTED_LOCALES as readonly string[]).includes(base)
    ? (base as Locale)
    : 'en';
};

export type EntryTipModalProps = {
  visible: boolean;
  exercise: Exercise | null;
  catalog: ReadonlyArray<Exercise> | null;
  /** Per-exercise program/plan instruction (DraftEntry.notes — carries
   *  Slot.instructions for program sessions). Surfaced read-only here so
   *  the coach's "how to do this exercise in this program" note is finally
   *  visible at log time (it isn't shown anywhere else). Empty = absent. */
  planNote?: string;
  onClose: () => void;
  /** Parent owns the swap mechanics (bilateral preference reload,
   *  cardio branching) so we delegate the actual mutation. */
  onPickAlternative: (alt: Exercise) => void;
  profile: import('./hostDeps').LoggerProfile;
  PainDot?: import('react').ComponentType<{ exercise: Exercise }>;
  /** AN-2 — host resolver for the exercise animation. Absent, or answering
   *  null, means this exercise has no GIF, which is the normal case for most
   *  of the catalog: the band below then shows the bodymap alone, exactly as
   *  it did before the pager existed. */
  animationSource?: AnimationSourceFn;
  /** AN-7: see hostDeps.onAnimationLoadError. Optional passthrough. */
  onAnimationLoadError?: () => Promise<void>;
  /** `PG-11` — the athlete's current MAX for this exercise, for the history
   *  page's header cue. Absent = none recorded, which is not an age. */
  max?: import('@heraion/shared').MaxForProposal;
  /** Open the host's MAX editor. ⛔ Absent = the header control is not drawn.
   *  A host showing no athlete's own 1RM (a coach editing a program) passes
   *  neither. */
  onEditMax?: () => void;
};

export function EntryTipModal({
  visible,
  exercise,
  catalog,
  planNote,
  onClose,
  onPickAlternative,
  profile,
  PainDot,
  animationSource,
  onAnimationLoadError,
  max,
  onEditMax,
}: EntryTipModalProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const getExerciseName = useExerciseName();
  const styles = makeStyles(theme);

  const locale = normaliseLocale(i18n.resolvedLanguage ?? i18n.language);
  // The history page names similar exercises by slug; the catalog this modal
  // already holds is what turns one into a word. ⛔ Falls back to the slug
  // rather than to an empty string: an unresolvable id is better read than
  // silently blank.
  const nameBySlug = useCallback(
    (slug: string, fallback?: string) => {
      const hit = catalog?.find((e) => e.slug === slug);
      return hit ? getExerciseName(hit.name) : (fallback ?? slug);
    },
    [catalog, getExerciseName],
  );

  const description = useMemo(
    () => (exercise ? pickDescription(exercise.description, locale) : null),
    [exercise, locale],
  );

  const bodyValue = useMemo(() => {
    if (!exercise) return {};
    return workedMusclesFromExercises([
      { primary: exercise.primary, secondary: exercise.secondary },
    ]);
  }, [exercise]);

  const alternatives = useMemo(() => {
    if (!exercise || !catalog) return [];
    return rankSwapCandidates(exercise, catalog).slice(0, ALT_LIMIT);
  }, [exercise, catalog]);

  const painRisk = useMemo(
    () => (exercise ? exercisePainSeverity(exercise, profile.painMap) : undefined),
    [exercise, profile.painMap],
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <SafeAreaView
        style={[styles.safe, { backgroundColor: theme.palette.bg }]}
        edges={['top', 'left', 'right', 'bottom']}
      >
        <View style={styles.header}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('workout.tipClose', {
              defaultValue: 'Close',
            })}
            onPress={onClose}
            hitSlop={8}
            style={styles.closeBtn}
          >
            <Text style={styles.close}>×</Text>
          </Pressable>
          <Text style={styles.title} numberOfLines={1}>
            {exercise ? getExerciseName(exercise.name) : ''}
          </Text>
          <View style={styles.headerSpacer} />
        </View>

        <ScrollView
          contentContainerStyle={styles.body}
          keyboardShouldPersistTaps="handled"
        >
          {painRisk ? (
            <View
              style={[
                styles.painBanner,
                {
                  backgroundColor: PAIN_SEVERITY_COLOURS[painRisk.severity],
                },
              ]}
            >
              <Text style={styles.painBannerTitle}>
                {t(`workout.painBanner.${painRisk.severity}`)}
              </Text>
              <Text style={styles.painBannerBody}>
                {painRisk.painfulMuscles.map((m, i) => {
                  const sep = i < painRisk.painfulMuscles.length - 1 ? ', ' : '';
                  const muscle = formatMuscleSlug(m.slug);
                  if (m.side === 'both') {
                    return (
                      <Text key={m.slug}>
                        {muscle}
                        {sep}
                      </Text>
                    );
                  }
                  const side = t(`bodymap.painSideOnly.${m.side}`);
                  return (
                    <Text key={m.slug}>
                      <Trans
                        i18nKey="bodymap.painBannerMuscleSide"
                        values={{ muscle, side }}
                        components={{ side: <Text style={styles.painBannerSide} /> }}
                      />
                      {sep}
                    </Text>
                  );
                })}
              </Text>
            </View>
          ) : null}

          {exercise?.pinnedNote ? (
            <View style={styles.noteBlock}>
              <Text style={styles.noteBlockLabel}>
                {t('workout.tipCoachNote', { defaultValue: 'Coach note' })}
              </Text>
              <Text style={styles.noteBlockText}>{exercise.pinnedNote}</Text>
            </View>
          ) : null}

          {planNote && planNote.trim().length > 0 ? (
            <View style={styles.noteBlock}>
              <Text style={styles.noteBlockLabel}>
                {t('workout.tipProgramNote', {
                  defaultValue: 'In this program',
                })}
              </Text>
              <Text style={styles.noteBlockText}>{planNote}</Text>
            </View>
          ) : null}

          {description ? (
            <Text style={styles.description}>{description}</Text>
          ) : null}

          {exercise && showsPerImplementHint(exercise) ? (
            <View style={styles.loadingTip}>
              <Text style={styles.loadingTipText}>
                {t('workout.perImplementHint', {
                  defaultValue:
                    'Using two, or one side at a time? Log the weight of just one. The app counts both (×2).',
                })}
              </Text>
            </View>
          ) : null}

          {Object.keys(bodyValue).length > 0 ? (
            <View style={styles.bodySection}>
              {/* ⭐ LG-59 — the third page he asked for: «una parte che volevo
                  nel card informazione dell'esercizio, come terza schermata
                  dopo animazione, body map [...] potrebbe benissimo essere il
                  contenuto del widget cronologia, e quindi se si fa bene è
                  una sola primitiva per entrambi». It IS the same primitive:
                  <HistoryPanel>, with no set in hand, so it carries no copy
                  affordance and reads as information. */}
              <ExerciseVisualPager
                slug={exercise?.slug ?? ''}
                exerciseName={exercise ? getExerciseName(exercise.name) : ''}
                animationSource={exercise ? animationSource : undefined}
                onAnimationLoadError={onAnimationLoadError}
                pageLabels={[
                  t('workout.showBodyMap', {
                    defaultValue: 'Show the muscles worked',
                  }),
                  t('mobile.logger.historyToggle', { defaultValue: 'History' }),
                ]}
                pages={[
                  (band) => (
                    <View key="map" style={styles.bodyMapWrap}>
                      <BodyMap
                        mode="display"
                        view="front"
                        value={bodyValue}
                        fitTo={band}
                        figure={profile.preferredFigure}
                        layout="dual"
                        theme={theme}
                      />
                    </View>
                  ),
                  // ⛔ Scrolls INSIDE the band instead of stretching it. The
                  // band is a fixed square now, and the history is the page
                  // that can outgrow it.
                  (band) => (
                    <ScrollView
                      key="history"
                      // ⛔ `width` as well as height, and it is not redundant.
                      // `ExerciseVisualPager.styles.page` centres its child
                      // (right for the animation and the bodymap, which are
                      // square), and since `LG-61` fixed the page to a square
                      // the history stopped stretching and drew itself in a
                      // narrow centred column — his smoke of 4.48.0: «la
                      // cronologia è diventata ancora più piccola in
                      // orizzontale». The band is the width it should have.
                      style={{ height: band, width: band }}
                      contentContainerStyle={styles.historyPage}
                    >
                      <HistoryPanel
                        exerciseId={exercise?.slug ?? ''}
                        max={max}
                        {...(onEditMax ? { onEditMax } : {})}
                        locale={locale}
                        unit={profile.unitsPreference}
                        effortScale={profile.effortScale}
                        getExerciseNameBySlug={nameBySlug}
                        theme={theme}
                        t={t}
                        rowLimit={CARD_SET_ROWS}
                      />
                    </ScrollView>
                  ),
                ]}
              />
              <ScaleLegend
                variant="dots"
                theme={theme}
                style={styles.legendRow}
                items={[
                  { color: WORKED_MUSCLE_COLOURS.primary, label: t('bodymap.workedPrimary') },
                  { color: WORKED_MUSCLE_COLOURS.secondary, label: t('bodymap.workedSecondary') },
                ]}
              />
            </View>
          ) : null}

          <View style={styles.altsSection}>
            <Text style={styles.altsLabel}>
              {t('workout.tipAlternatives')}
            </Text>
            {alternatives.length === 0 ? (
              <Text style={styles.altsEmpty}>
                {t('workout.tipNoAlternatives')}
              </Text>
            ) : (
              alternatives.map((cand) => (
                <Pressable
                  key={cand.exercise.slug}
                  accessibilityRole="button"
                  onPress={() => onPickAlternative(cand.exercise)}
                  style={({ pressed }) => [
                    styles.altRow,
                    pressed && styles.altRowPressed,
                  ]}
                >
                  <View style={styles.altLabel}>
                    {PainDot ? <PainDot exercise={cand.exercise} /> : null}
                    <Text style={styles.altName} numberOfLines={1}>
                      {getExerciseName(cand.exercise.name)}
                    </Text>
                  </View>
                  <Text style={styles.altChevron}>›</Text>
                </Pressable>
              ))
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}
